import {
    Dialog,
    DialogPanel,
    Transition,
    TransitionChild,
} from "@headlessui/react";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../context/auth";
import useIsMobile from "../../hooks/useIsMobile";
import {
    AnnouncementInteraction,
    IAnnouncement,
} from "../../interfaces/announcement.interface";
import {
    getCurrentAnnouncement,
    recordAnnouncementInteraction,
} from "../../services/announcement/announcement.service";
import AnnouncementModalView, { humanizeMessageType } from "./index.view";

// Above the layout drawer's z-[1000], otherwise the sidebar renders over it.
const MODAL_Z_INDEX = "z-[1100]";

const AnnouncementModal: React.FC = () => {
    const { authState } = useContext(AuthContext);
    const { isMobile } = useIsMobile();
    const [announcement, setAnnouncement] = useState<IAnnouncement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    // The interact endpoint is idempotent, so this only avoids a redundant
    // request under strict-mode double effects.
    const viewedId = useRef<number | null>(null);

    const userId = authState.user?.id;
    // Announcements are authored by super-admins FOR tenant users, so the
    // admin portal never shows them — the author is not the audience.
    const isOrganizationUser = authState.user?.attributes?.type === "user";

    useEffect(() => {
        if (authState.loading || !userId || !isOrganizationUser) return;

        let cancelled = false;
        getCurrentAnnouncement().then((data) => {
            if (cancelled) return;
            setAnnouncement(data);
            setIsOpen(Boolean(data));
        });

        return () => {
            cancelled = true;
        };
    }, [authState.loading, userId, isOrganizationUser]);

    useEffect(() => {
        if (!isOpen || !announcement) return;
        if (viewedId.current === announcement.id) return;
        viewedId.current = announcement.id;
        // .catch keeps the fire-and-forget contract local instead of relying on
        // the service never rejecting.
        recordAnnouncementInteraction(announcement.id, "view").catch(() => {});
    }, [isOpen, announcement]);

    // Close first, track after: the modal must never wait on the network, and
    // a failed interact call is not the user's problem.
    const closeWith = (action: AnnouncementInteraction) => {
        const id = announcement?.id;
        setIsOpen(false);
        if (id) {
            recordAnnouncementInteraction(id, action).catch(() => {});
        }
    };

    // Fires for the × button, the backdrop and Esc.
    const handleDismiss = () => closeWith("dismiss");
    const handleAction = () => closeWith("click");

    if (!announcement) return null;

    const { attributes } = announcement;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog
                as="div"
                // The card's own <h2> is the visible title; this gives the
                // dialog the same accessible name without a second heading.
                aria-label={attributes.headline}
                className={`relative ${MODAL_Z_INDEX}`}
                onClose={handleDismiss}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className="fixed inset-0 backdrop-blur-[2px]"
                        style={{ backgroundColor: "rgba(16,24,40,.45)" }}
                        data-testid="announcement-overlay"
                    />
                </TransitionChild>

                <div className="fixed inset-0 overflow-y-auto">
                    <div
                        className={`flex min-h-full justify-center ${
                            isMobile ? "items-end" : "items-center p-4"
                        }`}
                    >
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom={
                                isMobile
                                    ? "opacity-0 translate-y-full"
                                    : "opacity-0 scale-95"
                            }
                            enterTo={
                                isMobile
                                    ? "opacity-100 translate-y-0"
                                    : "opacity-100 scale-100"
                            }
                            leave="ease-in duration-200"
                            leaveFrom={
                                isMobile
                                    ? "opacity-100 translate-y-0"
                                    : "opacity-100 scale-100"
                            }
                            leaveTo={
                                isMobile
                                    ? "opacity-0 translate-y-full"
                                    : "opacity-0 scale-95"
                            }
                        >
                            <DialogPanel
                                className={
                                    isMobile
                                        ? "w-full max-h-[82vh] overflow-y-auto transition-all"
                                        : "w-full max-w-[560px] transition-all"
                                }
                            >
                                <AnnouncementModalView
                                    messageType={attributes.message_type}
                                    kicker={humanizeMessageType(
                                        attributes.message_type
                                    )}
                                    headline={attributes.headline}
                                    body={attributes.body}
                                    buttonLabel={attributes.button_label}
                                    linkText={attributes.link_text}
                                    linkUrl={attributes.link_url}
                                    imageUrl={attributes.image_url}
                                    publishedAt={attributes.published_at}
                                    isMobile={isMobile}
                                    className={
                                        isMobile
                                            ? "w-full"
                                            : "w-full max-h-[96vh] overflow-y-auto"
                                    }
                                    onAction={handleAction}
                                    onLinkClick={handleAction}
                                    onClose={handleDismiss}
                                />
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AnnouncementModal;
